import React from "react";

import {
  faExternalLink,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { useFlow } from "src/api/queries";
import { DependencyList } from "src/base-components/DependencyList";
import { ExternalLink } from "src/base-components/ExternalLink";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { FlowVersionStatus, WorkspaceDataplane } from "src/clients/flow-api";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import {
  agentVersionsToDependencyItems,
  DependencyModalSkeleton,
  flowParentsToDependencyItems,
} from "src/flow/modals/utils";
import { DraftPill, PublishedPill } from "src/flow/Pills";
import {
  useDraftAgentsDependingOnFlow,
  usePublishedAgentsDependingOnFlow,
} from "src/flow/useAgentDependencies";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDeferredLoading } from "src/hooks/useDeferredLoading";
import { useJobs } from "src/jobs/api/queries";
import {
  DashboardPageParams,
  getBaseUrl,
  getUrlToJobPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type DeleteFlowModalProps = {
  isOpen: boolean;
  workspace?: WorkspaceDataplane;
  flowId?: string;
  flowSlug?: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

type BlockingReason =
  | "has_active_jobs"
  | "has_published_versions"
  | "has_published_deps";

type ModalState = "loading" | "error" | BlockingReason | "confirmation" | false;

export const DeleteFlowModal: React.FC<DeleteFlowModalProps> = ({
  isOpen,
  workspace,
  flowId,
  flowSlug,
  onClose,
  onConfirm,
}) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { flows: flowCapabilities } = useCapabilities();
  const { data: activeJobs } = useJobs({
    baseUrl: workspace?.base_url ?? "",
    flowId: flowId,
    status: "active",
  });
  const {
    data: flow,
    isLoading: isLoadingFlow,
    isFetching: isFetchingFlow,
  } = useFlow(flowId);

  // Lazy agent deps
  const {
    data: publishedAgentVersions,
    isError: isPublishedError,
    refetch: refetchPublished,
  } = usePublishedAgentsDependingOnFlow(flowSlug, { enabled: isOpen });
  const {
    data: draftAgentVersions,
    isError: isDraftError,
    refetch: refetchDraft,
  } = useDraftAgentsDependingOnFlow(flowSlug, { enabled: isOpen });

  const hasAgentDepsError = isPublishedError || isDraftError;
  const retryAgentDeps = () => {
    if (isPublishedError) refetchPublished();
    if (isDraftError) refetchDraft();
  };

  const agentDepsResolved =
    publishedAgentVersions !== undefined && draftAgentVersions !== undefined;
  const { showIndicator, suppress } = useDeferredLoading(
    !agentDepsResolved || isFetchingFlow,
    isOpen,
  );

  if (!flow && !isLoadingFlow) return null;

  const hasLiveVersions =
    flow?.versions.some((v) => v.status === FlowVersionStatus.PUBLISHED) ??
    false;

  // Published flow parent items (across all versions), deduplicated
  const uniquePublishedFlowItems = [
    ...new Map(
      (flow?.versions ?? [])
        .flatMap((version) =>
          flowParentsToDependencyItems({
            flowVersion: version,
            orgId,
            wsId,
            statusFilter: "published",
          }),
        )
        .map((item) => [item.id, item] as const),
    ).values(),
  ];

  // Published agent items
  const publishedAgentItems = agentVersionsToDependencyItems(
    publishedAgentVersions ?? [],
    orgId,
    wsId,
    <PublishedPill />,
  );

  const allPublishedItems = [
    ...uniquePublishedFlowItems,
    ...publishedAgentItems,
  ];
  const hasPublishedDeps = allPublishedItems.length > 0;

  // FYI items: all non-archived flow parent deps (across all versions), deduplicated
  const uniqueAllFlowItems = [
    ...new Map(
      (flow?.versions ?? [])
        .flatMap((version) =>
          flowParentsToDependencyItems({
            flowVersion: version,
            orgId,
            wsId,
            filterArchivedVersions: true,
          }),
        )
        .map((item) => [item.id, item] as const),
    ).values(),
  ];

  const draftAgentItems = agentVersionsToDependencyItems(
    draftAgentVersions ?? [],
    orgId,
    wsId,
    <DraftPill />,
  );

  const fYIItems = [...uniqueAllFlowItems, ...draftAgentItems];

  const determineModalState = (): ModalState => {
    if (!isOpen) return false;
    if (suppress) return false;
    if (hasAgentDepsError) return "error";
    if (showIndicator) return "loading";
    if ((activeJobs?.length ?? 0) > 0) return "has_active_jobs";
    if (hasLiveVersions && !flowCapabilities.canDeletePublished)
      return "has_published_versions";
    if (hasPublishedDeps) return "has_published_deps";
    if (!agentDepsResolved) return "loading";
    return "confirmation";
  };

  const modal = determineModalState();

  const blockingDescriptions: Record<BlockingReason, string> = {
    has_active_jobs:
      "This flow cannot be deleted as it is being used by these active jobs:",
    has_published_versions:
      "This flow cannot be deleted as it has published versions.",
    has_published_deps:
      "This flow cannot be deleted because it is used by published versions of other resources:",
  };

  return (
    <>
      {/* Loading skeleton */}
      <Modal open={modal === "loading"} size="sm" onClose={onClose}>
        <Modal.Header>{`Delete "${flow?.name ?? "flow"}"`}</Modal.Header>
        <DependencyModalSkeleton />
      </Modal>

      {/* Error loading agent deps */}
      <Modal open={modal === "error"} size="sm" onClose={onClose}>
        <Modal.Header>{`Delete "${flow?.name ?? "flow"}"`}</Modal.Header>
        <Modal.Content>
          <EmptyState
            actionButton={{ text: "Retry", onClick: retryAgentDeps }}
            description="Please try again."
            headline="Failed to load dependencies"
            icon={faTriangleExclamation}
            size="compact"
            variant="error"
          />
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Blocking: active jobs */}
      <Modal open={modal === "has_active_jobs"} onClose={onClose}>
        <Modal.Header description={blockingDescriptions.has_active_jobs}>
          {`Cannot delete "${flow?.name}"`}
        </Modal.Header>
        <Modal.Content>
          <ul className="mb-2.5 list-disc pl-4">
            {activeJobs?.map((job) => (
              <li key={job.id} className="mb-1 font-inter-medium-13px">
                <ExternalLink
                  className="flex items-center text-gray-800"
                  href={
                    getBaseUrl() +
                    getUrlToJobPage(
                      orgId,
                      workspace?.id ?? "",
                      flow?.id ?? "",
                      job.id,
                    )
                  }
                >
                  {job.name}
                  <Icon icon={faExternalLink} size="2xs" />
                </ExternalLink>
              </li>
            ))}
          </ul>
          <p className="font-inter-normal-13px text-gray-500">
            Please deactivate these jobs before deleting this Decision Flow.
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Blocking: published versions without permission */}
      <Modal open={modal === "has_published_versions"} onClose={onClose}>
        <Modal.Header description={blockingDescriptions.has_published_versions}>
          {`Cannot delete "${flow?.name}"`}
        </Modal.Header>
        <Modal.Content>
          <p className="font-inter-normal-13px text-gray-500">
            This flow can only be deleted by an admin as it has published
            versions.
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Blocking: published dependencies */}
      <Modal open={modal === "has_published_deps"} onClose={onClose}>
        <Modal.Header description={blockingDescriptions.has_published_deps}>
          {`Cannot delete "${flow?.name}"`}
        </Modal.Header>
        <Modal.Content>
          <DependencyList
            dataLoc="flow-published-dependencies"
            items={allPublishedItems}
          />
          <p className="mt-2.5 font-inter-normal-13px text-gray-500">
            Please remove these connections before deleting this flow.
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Confirmation with FYI — only mount once agent deps resolved to prevent flash */}
      {agentDepsResolved && (
        <TextConfirmationModal
          additionalContent={
            fYIItems.length > 0 ? (
              <div className="mb-4">
                <DependencyList dataLoc="flow-dependencies" items={fYIItems} />
              </div>
            ) : undefined
          }
          calloutPill={
            hasLiveVersions
              ? {
                  type: "error",
                  text: "This Decision Flow contains published versions. Deleting these will remove all live data and any currently active processes relying on these versions.",
                }
              : undefined
          }
          challengeText={flow?.name}
          description={
            fYIItems.length > 0
              ? "Are you sure you want to delete this flow? This action cannot be undone. Deleting this flow will break the connections listed below."
              : "Are you sure you want to delete this flow? This action cannot be undone."
          }
          label="Please enter the Flow's name to confirm."
          open={modal === "confirmation"}
          title={`Delete "${flow?.name ?? ""}"`}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
};
